import { ChangeDetectorRef, Component, Input, OnInit , ChangeDetectionStrategy } from '@angular/core';
import IDashboard from 'src/app/interfaces/strapi/dashboard.interface';
import { filter } from 'rxjs';
import { ActivatedRoute, NavigationEnd, Router, RouterLink, RouterLinkActive } from '@angular/router';
import StrapiService from 'src/app/services/strapi.service';
import { NgIf, NgFor } from '@angular/common';
import { AuthService } from '../../../services/auth.service';
import { User } from '../../models/user.model';
import {
  Header,
  IStrapiDocument,
  IStrapiDocuments,
  Link,
  Menu,
} from '../../../interfaces/strapi';
import { UniService } from '../../../services/uni.service';
import { BackButtonComponent } from '../back-button/back-button.component';
import { CmsLinkComponent } from '../cms-link/cms-link.component';

@Component({
    selector: 'app-header',
    templateUrl: './header.component.html',
    styleUrls: ['./header.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        BackButtonComponent,
        NgIf,
        RouterLink,
        RouterLinkActive,
        NgFor,
        CmsLinkComponent,
    ],
})
export default class HeaderComponent implements OnInit {
  @Input() public pageScrolled = false;

  public headerData: Header | undefined;

  public mobileMainMenu: Menu | undefined;

  public navigationMenu: Menu | undefined;

  public mobileMyProfile: Link | undefined;

  public mobileMyInsurance: Link | undefined;

  public showMobileMenu = false;

  public showWellbeing = false;

  public showJobsAndCareers = false;

  public marketingPage = false;

  public desktopMenuError = false;

  public mobileMenuError = false;

  public navigationMenuError = false;

  public showInternationalStudents: boolean = false;

  constructor(
    public readonly auth: AuthService,
    public readonly router: Router,
    private readonly cd: ChangeDetectorRef,
    private readonly activatedRoute: ActivatedRoute,
    private readonly strapi: StrapiService,
    public readonly uniService: UniService
  ) {
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => {
        this.activatedRoute.firstChild?.data.subscribe((data) => {
          this.deferUpdate(() => {
            this.marketingPage = !!data['isMarketingPage'];
          });
        });
        this.showMobileMenu = false;
      });
  }

  private deferUpdate(fn: () => void): void {
    Promise.resolve().then(() => {
      fn();
      this.cd.markForCheck();
    });
  }

  /**
   * Get dashboard, header and current user
   */
  ngOnInit(): void {
    this.showWellbeing = !!this.auth.currentUser?.accommodation?.wellbeing;

    this.strapi.getDashboard().subscribe({
      next: (dashboard: IStrapiDocument<IDashboard>) => {
        this.deferUpdate(() => {
          this.showJobsAndCareers =
            dashboard.data.attributes.enable_jobs_and_careers;
        });
      },
      complete: () => {
        this.getMobileMenuItems();
        this.getNavigationBar();
      },
    });

    this.strapi.getHeaders().subscribe((data: IStrapiDocuments<Header>) => {
      this.deferUpdate(() => {
        this.headerData = this.parseHeaderData(data.data[0].attributes);
      });
    });

    // subscribe to the new user subject so the nav can update if they change accommodation
    this.auth.getCurrentUser().subscribe({
      next: (theUser: User) => {
        if (theUser) {
          this.deferUpdate(() => {
            this.showWellbeing = !!theUser.accommodation?.wellbeing;
          });
          this.getNavigationBar();
          this.getMobileMenuItems();
        }
      },
    });
  }

  public getMobileMenuItems(): void {
    this.strapi.getMainMobileMenu().subscribe({
      next: (data: IStrapiDocuments<Menu>) => {
        this.deferUpdate(() => {
          this.parseMobileMenu(data.data[0].attributes);
        });
      },
      error: () => {
        this.deferUpdate(() => {
          this.mobileMenuError = true;
        });
      },
    });
  }

  public getNavigationBar(): void {
    this.strapi.getNavigationMenubar().subscribe({
      next: (data: IStrapiDocuments<Menu>) => {
        this.deferUpdate(() => {
          this.navigationMenu = this.parseMenu(data.data[0].attributes);
        });
      },
      error: () => {
        this.deferUpdate(() => {
          this.navigationMenuError = true;
        });
      },
    });
  }

  public toggleNav(): void {
    this.showMobileMenu = !this.showMobileMenu;
  }

  private parseMenu(menuData: Menu): Menu {
    const parsed = <Menu>{};

    if (menuData.menu_items) {
      parsed.menu_items = this.filterMenuItems(menuData.menu_items);
    }

    return parsed;
  }

  private parseMobileMenu(menuData: Menu): void {
    const parsed = <Menu>{};

    if (menuData.menu_items) {
      parsed.menu_items = this.filterMenuItems(menuData.menu_items);

      // Take out My Insurance and My Profile as they have their own buttons
      // Identify these by route so that the label can be changed
      this.mobileMyProfile = parsed.menu_items.filter(
        (item) => item.route?.toLowerCase() === '/app/my-profile'
      )[0];
      this.mobileMyInsurance = parsed.menu_items.filter(
        (item) => item.route?.toLowerCase() === '/app/my-insurance'
      )[0];

      parsed.menu_items = parsed.menu_items.filter(
        (item) =>
          !item.route ||
          !['/app/my-profile', '/app/my-insurance'].includes(
            item.route.toLowerCase()
          )
      );
    }

    this.mobileMainMenu = parsed;
  }

  /**
   * Will filter out any menu items we don't want depending on what value are set in the cms
   *
   * @param Link[] menuItems
   * @returns
   */
  private filterMenuItems(menuItems: Link[]): Link[] {
    if (menuItems) {
      return (
        menuItems.filter((item: Link) => {
          if (
            item.route?.includes('international') &&
            !this.auth.currentUser?.university?.is_pathway_provider
          ) {
            return null;
          }

          if (item.route?.includes('jobs') && !this.showJobsAndCareers) {
            return null;
          }

          if (item.route?.includes('wellbeing') && !this.showWellbeing) {
            return null;
          }

          if (
            this.auth.currentUser?.university?.is_unihome_product &&
            item.route?.includes('protect')
          ) {
            return null;
          }

          if (
            this.auth.currentUser?.university?.is_unihome_product &&
            item.route?.includes('insurance')
          ) {
            return null;
          }

          return item;
        }) ?? ([] as Link[])
      );
    }
    return [] as Link[];
  }

  private parseHeaderData(page: Header): Header {
    const parsed = <Header>{};
    const theData = page;

    if (theData.logo) parsed.logo = theData.logo;

    return parsed;
  }

  public disableLogoLink() {
    return !!localStorage.getItem('unihomesFlow');
  }

  public get showMyInsuranceButton(): boolean {
    return (
      this.auth.initialDataFetched &&
      !(
        this.auth.currentUser?.university?.is_unihome_product &&
        !this.auth.hasStudentGuardInsurance &&
        !this.auth.currentUser?.insuranceProducts?.length
      )
    );
  }
}
